import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { users } from '@/api/users';
import { resellers } from '@/api/resellers';
import { customers } from '@/api/customers';
import { useAuth } from '@/lib/auth-store';
import { allowedTargetRoles, ROLE_LABEL } from '@/lib/roles';
import { PageHeader } from '@/components/PageHeader';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InviteMethodToggle, type InviteMethod } from '@/components/InviteMethodToggle';
import { PhoneInput } from '@/components/PhoneInput';
import type { Role } from '@/lib/auth-store';
import { Lock } from 'lucide-react';

const schema = z.object({
  email: z.string().email('Valid email required'),
  displayName: z.string().optional(),
  phone: z.string().optional(),
  role: z.enum(['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user', 'limited_user'] as const),
});
type V = z.infer<typeof schema>;

interface PrescopedState {
  customerId?: string;
  resellerId?: string;
  customerName?: string;
  resellerName?: string;
}

function usePrescope(): PrescopedState {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state ?? {}) as PrescopedState;
  const customerId = state.customerId ?? searchParams.get('customerId') ?? undefined;
  const resellerId = state.resellerId ?? searchParams.get('resellerId') ?? undefined;
  return {
    customerId,
    resellerId,
    customerName: state.customerName,
    resellerName: state.resellerName,
  };
}

export function UserNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);
  const prescope = usePrescope();
  const [serverError, setServerError] = useState<string | null>(null);
  const [method, setMethod] = useState<InviteMethod>('email_invite');
  const [tempPassword, setTempPassword] = useState('');

  // Fetch scoped tenant name if we only got an id (e.g. direct-linked URL with query param)
  const resellerQuery = useQuery({
    queryKey: ['reseller', prescope.resellerId],
    queryFn: () => resellers.get(prescope.resellerId!),
    enabled: Boolean(prescope.resellerId) && !prescope.resellerName && !!me?.is_super_admin,
  });
  const customerQuery = useQuery({
    queryKey: ['customer', prescope.customerId],
    queryFn: () => customers.get(prescope.customerId!),
    enabled: Boolean(prescope.customerId) && !prescope.customerName,
  });

  const resolvedCustomerName = prescope.customerName ?? customerQuery.data?.name ?? null;
  const resolvedResellerName = prescope.resellerName ?? resellerQuery.data?.name ?? null;

  // When scoped to a customer we still need its reseller_id for the backend call
  const effectiveResellerId = useMemo(() => {
    if (prescope.resellerId) return prescope.resellerId;
    if (customerQuery.data) return customerQuery.data.reseller_id;
    return undefined;
  }, [prescope.resellerId, customerQuery.data]);

  const allowed = me && tenant
    ? allowedTargetRoles({ role: me.role, isSuperAdmin: tenant.isSuperAdmin })
    : [];

  const firstAllowed = allowed[0] ?? 'standard_user';
  const { register, control, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<V>({
    resolver: zodResolver(schema),
    defaultValues: { role: firstAllowed },
  });

  const isScoped = Boolean(prescope.customerId || prescope.resellerId);
  const scopeLabel = prescope.customerId
    ? `Customer · ${resolvedCustomerName ?? '…'}`
    : prescope.resellerId
      ? `Reseller · ${resolvedResellerName ?? '…'}`
      : null;

  const create = useMutation({
    mutationFn: (values: V) =>
      users.create({
        email: values.email,
        role: values.role as Role,
        // Scope precedence: explicit pre-scope from route state/query → resolved
        // reseller of a scoped customer → caller's own tenant scope (for a
        // reseller_owner or office_manager using the top-level /users/new,
        // they always mean "into my tenant"). Super-admin with no scope gets
        // nulls, which the backend accepts for role=super_admin targets.
        resellerId: prescope.resellerId ?? effectiveResellerId ?? tenant?.resellerId ?? null,
        customerId: prescope.customerId ?? tenant?.customerId ?? null,
        displayName: values.displayName ?? null,
        phone: values.phone ?? null,
        method,
        manualPassword: method === 'manual_password' ? tempPassword : undefined,
      }),
    onSuccess: () => {
      // Navigate back to wherever the scope lives so the admin sees context retained
      if (prescope.customerId) navigate(`/customers/${prescope.customerId}`);
      else if (prescope.resellerId) navigate(`/resellers/${prescope.resellerId}`);
      else navigate('/users');
    },
  });

  async function onSubmit(values: V) {
    setServerError(null);
    if (isScoped && !effectiveResellerId) {
      setServerError(t('users.noPermission'));
      return;
    }
    try {
      await create.mutateAsync(values);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'email_already_exists' || code === 'email_taken' || code === 'email_already_in_use') {
        setServerError('This email is already in use.');
      } else {
        setServerError(err instanceof Error ? err.message : t('common.failedToSave'));
      }
    }
  }

  if (allowed.length === 0) {
    return (
      <>
        <PageHeader title={t('users.inviteTitle')} />
        <p className="text-sm text-muted-foreground">{t('users.noPermission')}</p>
      </>
    );
  }

  const selectedRole = watch('role');

  return (
    <>
      <PageHeader
        eyebrow={t('nav.users')}
        title={t('users.inviteTitle')}
        description={t('users.inviteDescription')}
        actions={
          <Button variant="outline" asChild>
            <Link to={prescope.customerId ? `/customers/${prescope.customerId}` : prescope.resellerId ? `/resellers/${prescope.resellerId}` : '/users'}>
              {t('common.back')}
            </Link>
          </Button>
        }
      />

      <div className="max-w-2xl">
        {isScoped && scopeLabel && (
          <div className="mb-4 flex items-start gap-3 rounded-md border border-border bg-primary/5 p-4">
            <Lock className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <div className="text-sm">
              <p className="font-medium">{t('users.scopedInvite')}</p>
              <p className="text-muted-foreground">
                {t('users.scopedInviteDescription', { scope: scopeLabel })}
              </p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('users.newUserDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
              {/* Email */}
              <div className="space-y-1">
                <Label htmlFor="email">{t('users.emailLabel')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={errors.email != null}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <p id="email-error" role="alert" className="text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Display name */}
              <div className="space-y-1">
                <Label htmlFor="displayName">{t('users.displayNameOptional')}</Label>
                <Input
                  id="displayName"
                  type="text"
                  aria-invalid={errors.displayName != null}
                  aria-describedby={errors.displayName ? 'displayName-error' : undefined}
                  {...register('displayName')}
                />
                {errors.displayName && (
                  <p id="displayName-error" role="alert" className="text-xs text-destructive">
                    {errors.displayName.message}
                  </p>
                )}
              </div>

              {/* Phone — default prefix scoped to where the invite lands
                  (mirrors the create-payload scope precedence below). */}
              <div className="space-y-1">
                <Label htmlFor="phone">{t('users.phoneOptional')}</Label>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <PhoneInput
                      id="phone"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      customerId={prescope.customerId ?? tenant?.customerId ?? null}
                      resellerId={prescope.resellerId ?? effectiveResellerId ?? tenant?.resellerId ?? null}
                      placeholder="555 000 0000"
                      aria-invalid={errors.phone != null}
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                  )}
                />
                {errors.phone && (
                  <p id="phone-error" role="alert" className="text-xs text-destructive">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              {/* Role */}
              <div className="space-y-1">
                <Label htmlFor="role">{t('users.roleLabel')}</Label>
                <select
                  id="role"
                  className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-invalid={errors.role != null}
                  aria-describedby={errors.role ? 'role-error' : undefined}
                  value={selectedRole}
                  {...register('role')}
                >
                  {allowed.map((r) => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
                {errors.role && (
                  <p id="role-error" role="alert" className="text-xs text-destructive">
                    {errors.role.message}
                  </p>
                )}
              </div>

              {/* Invite method */}
              <InviteMethodToggle
                value={method}
                onChange={setMethod}
                tempPassword={tempPassword}
                onTempPasswordChange={setTempPassword}
              />

              {/* Server error */}
              {serverError && (
                <p role="alert" className="text-sm text-destructive">
                  {serverError}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button type="submit" disabled={isSubmitting || create.isPending}>
                  {isSubmitting || create.isPending ? t('common.inviting') : t('users.inviteUser')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (prescope.customerId) navigate(`/customers/${prescope.customerId}`);
                    else if (prescope.resellerId) navigate(`/resellers/${prescope.resellerId}`);
                    else navigate('/users');
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
